import type { ClientLibraryDiscoveryPanelItem } from 'types/client-library'
import type { PlaylistCurated } from 'types/playlist'

export type ContentType = 'playlist' | 'catalog'

// id is required as all valid library items from the API have an id
export interface ClientLibrary
  extends Partial<Omit<ClientLibraryDiscoveryPanelItem, 'id'>> {
  id: ClientLibraryDiscoveryPanelItem['id']
  name?: string
}

export interface DiscoveryPanelItem {
  id?: string | number
  name: string
  display_name?: string
  client_libraries?: ClientLibrary[]
}

export interface PlaylistNavigationState {
  playlist: PlaylistCurated
}

export interface CatalogNavigationState {
  catalog: ClientLibraryDiscoveryPanelItem
}

export type NavigationState =
  | PlaylistNavigationState
  | CatalogNavigationState
  | null

export const getCatalogBusinessNameFromUrl = (library_url: string): string => {
  let businessUrl: string

  try {
    businessUrl =
      library_url && library_url.trim()
        ? new URL(library_url).hostname
        : 'DISCO catalog'
  } catch {
    businessUrl = 'DISCO catalog'
  }

  const businessName = businessUrl.split('.')[0].replace(/-/g, ' ')

  return businessName
}

export const getAccessButtonLabel = (
  hasAccess: boolean,
  status: ClientLibraryDiscoveryPanelItem['status'],
): string => {
  if (hasAccess) return 'Download access'
  if (status === 'pending' || status === 'invited') return 'Access pending'
  return 'Request access'
}

export const getAccessAriaLabel = (
  hasAccess: boolean,
  status: ClientLibraryDiscoveryPanelItem['status'],
): string => {
  if (hasAccess) return 'You have download access'
  if (status === 'pending' || status === 'invited') return 'Access pending'
  return 'Request access'
}

export const hasUserAccess = (
  status: ClientLibraryDiscoveryPanelItem['status'],
  accessLevel: ClientLibraryDiscoveryPanelItem['discovery_access_level'],
): boolean => {
  return status === 'approved' || accessLevel === 'discovery_easy_access'
}

// Client-side group filtering over the fields available on the grouped payload (display_name, name, short_description).
export const matchesDiscoverySearchQuery = (
  library:
    | {
        display_name?: string | null
        name?: string | null
        short_description?: string | null
      }
    | null
    | undefined,
  query: string,
): boolean => {
  if (!library) return false

  const lowerQuery = query.toLowerCase()
  const displayName = (library.display_name || '').toLowerCase()
  const name = (library.name || '').toLowerCase()
  const shortDescription = (library.short_description || '').toLowerCase()

  return (
    displayName.includes(lowerQuery) ||
    name.includes(lowerQuery) ||
    shortDescription.includes(lowerQuery)
  )
}

export const canRequestAccess = (
  hasAccess: boolean,
  status: ClientLibraryDiscoveryPanelItem['status'],
): boolean => {
  return !hasAccess && status !== 'pending' && status !== 'invited'
}

export interface ContentDisplayValues {
  title: string
  image: string | null | undefined
  description: string | undefined
  businessName: string | undefined
  curatedBy: string | undefined
}

export type WaveformData = number[] | { samples: number[] }

/**
 * Parses waveform data from a track, handling both string and object formats.
 * Returns null if parsing fails or data is invalid.
 */
export const parseWaveformData = (
  waveform: string | WaveformData | null | undefined,
): WaveformData | null => {
  if (!waveform) return null

  try {
    if (typeof waveform === 'string') {
      return JSON.parse(waveform)
    }
    return waveform
  } catch (e) {
    console.warn('Failed to parse track waveform data:', e)
    return null
  }
}

export const getDisplayValues = (
  contentType: ContentType,
  playlist: PlaylistCurated | null,
  catalog: ClientLibraryDiscoveryPanelItem | null,
): ContentDisplayValues => {
  if (contentType === 'playlist') {
    // Cast description to string - API always returns string, but type allows ReactNode
    const description =
      typeof playlist?.description === 'string'
        ? playlist.description
        : undefined
    return {
      title: playlist?.name || 'Playlist',
      image: playlist?.image,
      description,
      businessName: playlist?.business?.business_name,
      curatedBy: playlist?.curated_by,
    }
  }

  return {
    title: catalog?.display_name || 'DISCO catalog',
    image: catalog?.discovery_image_url || catalog?.image_logo_url,
    description: catalog?.short_description,
    businessName: undefined,
    curatedBy: undefined,
  }
}
